import React, { useMemo } from 'react';
import {
  DataCell,
  Flex,
  HoverCardContent,
  HoverCardRoot,
  HoverCardTrigger,
  Text,
} from '@/components/ui';
import { DataList } from '@/components/ui/DataList';
import { formatVersionNumber } from '@/utils/csvFileUtils';
import { CsvFileVersion } from '@/types/csvFiles';
import StatusCell from './StatusCell';
import { IconDatabaseImport } from '@tabler/icons-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

interface VersionImportInfoCellProps {
  value?: unknown;
  version?: CsvFileVersion;
  render?: () => React.ReactNode;
}

function VersionImportInfoCell({
  version,
  render,
}: VersionImportInfoCellProps) {
  const versionNumber = version
    ? formatVersionNumber(version.version_number, version.id)
    : '-';

  const relativeTimeText = useMemo(() => {
    if (!version?.imported_at) return 'Not Imported';
    const date = dayjs(version.imported_at);
    if (!date.isValid()) {
      return 'Invalid date';
    }
    // Use relative time format (same as DateTimeCell when relative=true)
    return date.fromNow();
  }, [version?.imported_at]);

  if (render) {
    return <>{render()}</>;
  }

  if (!version) {
    return (
      <Text size="2" color="gray">
        -
      </Text>
    );
  }

  // If version is not imported, show clear label
  // Consider imported if imported_at is present (imported_by can be null)
  if (!version.imported_at) {
    return (
      <Text size="2" color="gray">
        Not Imported
      </Text>
    );
  }

  const importedCell = (
    <Flex align="center" gap="2" style={{ display: 'inline-flex' }}>
      <IconDatabaseImport size={16} style={{ color: 'var(--green-9)' }} />
      <Text
        size="2"
        color="gray"
        style={{
          cursor: 'pointer',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: '2px',
        }}
      >
        {relativeTimeText}
      </Text>
    </Flex>
  );

  // Wrap in HoverCard with full information
  return (
    <HoverCardRoot>
      <HoverCardTrigger>
        <div style={{ display: 'inline-flex' }}>{importedCell}</div>
      </HoverCardTrigger>
      <HoverCardContent size="2">
        <Flex direction="column" gap="3">
          <Flex align="center" gap="2">
            <StatusCell
              value="approved"
              entityType="csv_file"
              status="approved"
              size="small"
              label="Imported"
            />
          </Flex>
          <DataList.Root
            style={{
              gap: 'var(--space-4) var(--space-2)',
            }}
          >
            <DataList.Item>
              <DataList.Label>Version</DataList.Label>
              <DataList.Value>{versionNumber}</DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>Imported</DataList.Label>
              <DataList.Value>
                <DataCell type="datetime" value={version.imported_at} />
              </DataList.Value>
            </DataList.Item>
            {version.imported_by && (
              <DataList.Item>
                <DataList.Label>Imported By</DataList.Label>
                <DataList.Value>
                  <DataCell type="user" value={version.imported_by} />
                </DataList.Value>
              </DataList.Item>
            )}
          </DataList.Root>
        </Flex>
      </HoverCardContent>
    </HoverCardRoot>
  );
}

export default VersionImportInfoCell;

export function VersionImportInfoCellSkeleton() {
  return (
    <div className="data-cell__version-import-info-skeleton">
      <Flex align="center" gap="2">
        <div
          className="skeleton skeleton--text"
          style={{ width: '14px', height: '14px' }}
        />
        <div className="skeleton skeleton--text" style={{ width: '80px' }} />
      </Flex>
    </div>
  );
}
